import React, { FC, useState, useEffect, useRef, useCallback } from 'react';
import {
  Flex,
  Box,
  Divider,
  UseDisclosureProps,
  useDisclosure,
} from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { customReportActions } from '@/store/custom-report';
import { staticReportActions } from '@/store/static-report';
import HeaderActions from '@/pages/reports/custom-reports/show/headerActions';
import { TooltipText } from '@/ui/TooltipText';
import { KText } from '@/ui-library/components';
import { Link } from 'react-router-dom';
import { Edit05, LineChartUp03 } from '@kula-ai/icons-core';
import { LastRefreshedTime } from './components';
import { FiltersWrapper } from '@/pages/reports/custom-reports/show/FiltersWrapper';
import { ReportTypes } from '../../types';
import { REPORT_CONSTANTS } from '../../constants';
import { CreateEditCustomReportModal } from '@/pages/reports/custom-reports/components/CreateEditCustomReportModal';
import { useQueryClient } from '@tanstack/react-query';
import {
  getItem,
  setItem,
  useIsPreferencesLoaded,
} from '@/store/ServerStorage/serverStorage';
import { STORAGE_KEYS } from '@/utils/storageKeys';

interface ReportHeaderProps {
  reportData: ReportTypes.ReportMeta;
  isEditMode: boolean;
  hasOriginalTiles: boolean;
  setEditMode: (_isEditMode: boolean) => void;
  showAddBlockDisclosure: UseDisclosureProps;
  onFilterChange: (_filterType: string, _filterData: unknown) => void;
}

export const ReportHeader: FC<ReportHeaderProps> = React.memo(
  ({
    reportData,
    isEditMode,
    setEditMode,
    hasOriginalTiles,
    showAddBlockDisclosure,
    onFilterChange,
  }) => {
    const [localTitle, setLocalTitle] = useState<string>('');
    const dispatch = useDispatch();
    const {
      layoutData: report,
      defaultFilters,
      title,
      type,
      filtersToShow,
      isOwner,
      canModifyBlocks,
      isCustomReport,
    } = reportData;

    const queryClient = useQueryClient();
    const isPreferencesLoaded = useIsPreferencesLoaded();

    const isStaticReport = type === 'static';
    // Match FiltersWrapper's pattern: dispatch into the slice the tile fetch
    // actually reads from, so saved-view rehydrate works for static reports too.
    const { applyFilter, applyTimeFrame, applyViewBy } = isStaticReport
      ? staticReportActions
      : customReportActions;

    // Filter state managed locally in header
    const [selectedFilters, setSelectedFilters] = useState([]);
    const [isLocalFilterApplied, setIsLocalFilterApplied] = useState(false);

    const isLocalFilterAppliedRef = useRef(false);
    isLocalFilterAppliedRef.current = isLocalFilterApplied;

    const handleSetSelectedFilters = useCallback(
      (filters: unknown[]) => {
        setSelectedFilters(filters);
        // Guard: only persist after FiltersWrapper has finished its own
        // initialisation. Without this, the init call would overwrite the
        // user's previously saved filters in server storage.
        if (!isEditMode && isLocalFilterAppliedRef.current) {
          const storageKey = STORAGE_KEYS.getReportFiltersKey(
            reportData?.reportId
          );
          if (storageKey) setItem(storageKey, filters);
        }
      },
      [isEditMode, reportData?.reportId]
    );

    const showUpdateReportModal = useDisclosure();

    const reportTitle =
      localTitle || report?.name || report?.title || title || '';
    const showEditIcon = isOwner && reportTitle && isCustomReport; // Only show edit for custom reports

    const reportTitleRef = useRef(reportTitle);
    reportTitleRef.current = reportTitle;
    const reportRef = useRef(report);
    reportRef.current = report;
    const reportIdRef = useRef(reportData?.reportId);
    reportIdRef.current = reportData?.reportId;

    // Only run on actual unmount - no dependencies
    useEffect(() => {
      return () => {
        const reportId = reportIdRef.current;
        const latestReport = reportRef.current;
        const latestTitle = reportTitleRef.current;

        // on unmount, update the report name in the cache with latest one
        if (reportId && latestReport) {
          queryClient.setQueryData([`getCustomReports/${reportId}`], {
            data: { ...latestReport, name: latestTitle },
            status: 'success',
          });
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Derive local title: once report data arrives and no user edit has occurred,
    // sync it during render instead of via useEffect (avoids an extra render cycle).
    if (report?.name && !localTitle) {
      setLocalTitle(report.name);
    }

    // Listen for title updates from the modal
    useEffect(() => {
      const handleTitleUpdate = (event: CustomEvent) => {
        setLocalTitle(event.detail.newTitle);
      };

      const reportContainer = document.querySelector('[data-report-container]');
      if (reportContainer) {
        reportContainer.addEventListener(
          'title-updated',
          handleTitleUpdate as EventListener
        );
        return () => {
          reportContainer.removeEventListener(
            'title-updated',
            handleTitleUpdate as EventListener
          );
        };
      }
    }, []);

    // Restore saved view filters from server storage once preferences are loaded
    // and FiltersWrapper has finished its own initialisation (isLocalFilterApplied = true).
    // Uses the raw state setter so the restore itself doesn't trigger another save.
    // We also re-dispatch to Redux: FiltersWrapper's init only pushes
    // `defaultFilters` (the creator's defaults) into the slice that the tile fetch
    // reads from. Without this dispatch the chip rehydrates from storage but the
    // request payload omits filter_by.<saved>.
    useEffect(() => {
      const storageKey = STORAGE_KEYS.getReportFiltersKey(reportData?.reportId);
      if (!isPreferencesLoaded || !storageKey || !isLocalFilterApplied) return;
      // Skip in edit mode — the L193 reset effect owns filter state when
      // editing defaults. For static and custom reports alike, the dispatch
      // below targets whichever slice (static-report / custom-report) the tile
      // fetch reads from, so the saved-view payload reaches the request.
      if (isEditMode) return;
      const savedFilters = getItem(storageKey);
      if (
        savedFilters &&
        Array.isArray(savedFilters) &&
        savedFilters.length > 0
      ) {
        setSelectedFilters(savedFilters);
        const filterData = savedFilters.reduce(
          (acc, { filter_by, filter_condition, filter_by_values }) => {
            if (filter_by_values?.length) {
              acc[filter_by.value] = {
                [filter_condition.value]: filter_by_values.map(({ id }) => id),
              };
            }
            return acc;
          },
          {} as Record<string, Record<string, unknown[]>>
        );
        dispatch(applyFilter(filterData));
      }
    }, [
      isPreferencesLoaded,
      reportData?.reportId,
      isLocalFilterApplied,
      isEditMode,
      dispatch,
      applyFilter,
    ]);

    // Reset filters to defaultFilters when entering edit mode
    useEffect(() => {
      if (isEditMode && defaultFilters) {
        // Reset normal filters if different
        if (defaultFilters.normalFilters) {
          const currentFiltersString = JSON.stringify(selectedFilters);
          const defaultFiltersString = JSON.stringify(
            defaultFilters.normalFilters
          );

          if (currentFiltersString !== defaultFiltersString) {
            setSelectedFilters(defaultFilters.normalFilters);
          }
        }

        // Reset timeframe if different
        if (defaultFilters.timeframe) {
          dispatch(applyTimeFrame({ ...defaultFilters.timeframe }));
        }

        // Reset dateGroup if different
        if (defaultFilters.dateGroup) {
          const dateGroupId =
            typeof defaultFilters.dateGroup === 'object'
              ? defaultFilters.dateGroup.id
              : defaultFilters.dateGroup;

          if (dateGroupId) {
            dispatch(applyViewBy({ id: dateGroupId }));
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode, defaultFilters, dispatch, applyTimeFrame, applyViewBy]);

    const renderBreadcrumb = () => {
      return (
        <Link
          to={REPORT_CONSTANTS.COPY_CHANGES.BREADCRUMB_TO}
          style={{
            display: 'inline-block',
            width: 'fit-content',
            height: '20px',
          }}
        >
          <Flex alignItems="center" gap="6px" w="fit-content">
            <LineChartUp03 size={14} />
            <KText size="sm-regular" color="secondary-700" h="20px">
              {REPORT_CONSTANTS.COPY_CHANGES.BREADCRUMB_PATH}
            </KText>
          </Flex>
        </Link>
      );
    };

    const renderTitle = () => {
      return (
        <>
          <TooltipText
            stringLength={50}
            text={reportTitle}
            as="span"
            size="xl-medium"
            h="24px"
            color="secondary-900"
            variant="default"
          />
          {showEditIcon && (
            <Edit05
              size={16}
              className="!text-primary-900 cursor-pointer mt-0.5"
              onClick={showUpdateReportModal.onOpen}
            />
          )}
        </>
      );
    };

    const renderTags = () => {
      return (
        <Flex alignItems="center" gap="6px" pl="6px">
          {hasOriginalTiles && <LastRefreshedTime scope="reports" />}
          {isEditMode && (
            <Flex
              bg="pastel-6-light"
              p="4px 8px"
              alignItems="center"
              gap="6px"
              borderRadius="sm"
              flexShrink={0}
              w="fit-content"
              h="26px"
            >
              {/* <Edit05 size={16} className="!text-pastel-6dark" /> */}
              <KText size="sm-medium" color="pastel-6-dark" h="20px">
                You're in edit mode
              </KText>
            </Flex>
          )}
        </Flex>
      );
    };

    const getReportTypeForFilters = () => {
      const titleToReportType = {
        'Pipeline history': 'pipeline',
        Interviews: 'interviews',
        Offers: 'offers',
        Hires: 'hires',
        Rejections: 'rejections',
        Contacts: 'contacts',
        Jobs: 'jobs',
        EEO: 'eeo',
        'Recruiter performance': 'recruiter-performance',
        'Flow Health': 'flow-health',
        Requisitions: 'requisitions',
        'Current job pipeline': 'live_job_pipeline',
      };
      return titleToReportType[title] || 'pipeline';
    };

    return (
      <Box w="100%">
        {/* Header Section */}
        <Flex
          borderBottomWidth="1px"
          borderBottomColor="border-color"
          boxShadow="ats-shadow-3"
          justifyContent="center"
          p="12px 24px"
          w="100%"
          gap="3px"
          flexDir="column"
        >
          {/* Breadcrumb */}
          {renderBreadcrumb()}
          {/* Title, edit icon, last refreshed time and actions */}
          <Flex alignItems="center" justifyContent="space-between" w="100%">
            {/* left part */}
            <Flex alignItems="baseline" gap="6px" flex={1}>
              {renderTitle()}
              {renderTags()}
            </Flex>
            {/* right part */}
            {report?.id && (
              <HeaderActions
                customReport={report}
                initiateTileSelection={() => {
                  showAddBlockDisclosure?.onOpen();
                }}
                selectedFilters={selectedFilters}
                hasWriteAccess={canModifyBlocks}
                isOwner={isOwner}
                isStaticReport={isStaticReport}
                isEditMode={isEditMode}
                setEditMode={setEditMode}
              />
            )}
          </Flex>

          <Divider my="6px" />

          <FiltersWrapper
            selectedFilters={selectedFilters}
            setSelectedFilters={handleSetSelectedFilters}
            filtersToShow={filtersToShow as string[]}
            isLocalFilterApplied={isLocalFilterApplied}
            setIsLocalFilterApplied={setIsLocalFilterApplied}
            isStaticReport={isStaticReport}
            reportType={getReportTypeForFilters()}
            onFilterChange={isEditMode ? onFilterChange : undefined}
            defaultFilters={defaultFilters}
          />
        </Flex>

        {showUpdateReportModal.isOpen && (
          <CreateEditCustomReportModal
            type="edit"
            report={{ ...report, name: reportTitle }}
            onClose={showUpdateReportModal.onClose}
            submitButtonLabel="Update report"
            modalTitle={{
              headerText: 'Name of custom report',
              subHeaderText: `Give your report a unique name so it's easier to identify`,
            }}
            onSubmit={() => {
              showUpdateReportModal.onClose();
            }}
          />
        )}
      </Box>
    );
  }
);

ReportHeader.displayName = 'ReportHeader';
